/* eslint-disable typescript-extended-rules/file-naming-convention */
export enum ClaimUsageIngestionBlobStatus {
	STORED = 'ARMAZENADO',
}

export enum ClaimUsageIngestionJobStatus {
	COMPLETED = 'COMPLETED',
	CREATED = 'CREATED',
	FAILED = 'FAILED',
	PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
	PROCESSING = 'PROCESSING',
	QUEUED = 'QUEUED',
	UNKNOWN = 'UNKNOWN',
}

export type ClaimUsageIngestionOperator = 'CNU' | 'SULAMERICA';

export interface ClaimUsageIngestionUploadRequest {
	competence: string;
	file: File;
	operator: ClaimUsageIngestionOperator;
	organizationId: string;
	organizationName: string;
	responsible?: string;
	uploadedBy: string;
}

export interface ClaimUsageIngestionAcceptedResponse {
	detectedMetadata?: ClaimUsageIngestionDetectedMetadata;
	fileId?: string;
	jobId: string;
	status: ClaimUsageIngestionJobStatus;
	statusCode: 201 | 202;
}

export interface ClaimUsageIngestionDetectedMetadata {
	detectedBillingCompetence?: string;
	detectedDelimiter?: string;
	detectedFileCompetence?: string;
	detectedGroupCompanyCode?: string;
	detectedGroupCompanyName?: string;
	fileSize?: number;
	originalName?: string;
	storagePath?: string;
}

export interface ClaimUsageIngestionStatusResponse {
	error?: string;
	errorCode?: string;
	errorLines?: number;
	errorMessage?: string;
	errorSummary?: string;
	failedRows?: number;
	finishedAt?: string;
	jobId: string;
	lastUpdatedAt: string;
	processedLines?: number;
	processedRows?: number;
	startedAt: string;
	status: ClaimUsageIngestionJobStatus;
	totalRows?: number;
}

export interface ClaimUsageIngestionPreProcessing {
	blobStatus: ClaimUsageIngestionBlobStatus;
	competence: string;
	estimatedProcessingWindow: string;
	fileId?: string;
	jobId: string;
	jobStatus: ClaimUsageIngestionJobStatus;
	operator?: ClaimUsageIngestionOperator | string;
	operatorName: string;
	organizationId: string;
	organizationName: string;
	originalFileName: string;
	responsible?: string;
	size: number;
	sourceFile?: File;
	storagePath?: string;
	uploadedAt: string;
	uploadedBy: string;
}

export interface ClaimUsageIngestionPostProcessing {
	error?: string;
	errorCode?: string;
	errorLines?: number;
	errorMessage?: string;
	errorSummary?: string;
	failedRows?: number;
	finalStatus: ClaimUsageIngestionJobStatus;
	finishedAt?: string;
	lastUpdatedAt: string;
	processedLines?: number;
	processedRows?: number;
	startedAt: string;
	totalRows?: number;
}

export interface ClaimUsageIngestionHistoryItem {
	postProcessing: ClaimUsageIngestionPostProcessing | null;
	preProcessing: ClaimUsageIngestionPreProcessing;
}

export interface ClaimUsageIngestionUploadDialogResult {
	competence: string;
	file: File;
	operator: ClaimUsageIngestionOperator;
}

export interface ClaimUsageIngestionProcessingDetailData {
	errors?: ClaimUsageIngestionErrorItem[];
	postProcessing: ClaimUsageIngestionPostProcessing | null;
	preProcessing: ClaimUsageIngestionPreProcessing;
}

export interface ClaimUsageIngestionErrorItem {
	column?: string;
	errorCode?: string;
	errorSummary?: string;
	message: string;
	metadata?: unknown;
	rawLine?: string;
	rowNumber?: number;
}

export interface ClaimUsageIngestionErrorsResponse {
	items: ClaimUsageIngestionErrorItem[];
	limit: number;
	offset: number;
	total: number;
}

export const CLAIM_USAGE_INGESTION_ACTIVE_STATUSES = [
	ClaimUsageIngestionJobStatus.CREATED,
	ClaimUsageIngestionJobStatus.QUEUED,
	ClaimUsageIngestionJobStatus.PROCESSING,
];

export const CLAIM_USAGE_INGESTION_FINAL_STATUSES = [
	ClaimUsageIngestionJobStatus.COMPLETED,
	ClaimUsageIngestionJobStatus.PARTIALLY_COMPLETED,
	ClaimUsageIngestionJobStatus.FAILED,
];

export interface ClaimUsageIngestionErrorPresentationSource {
	error?: string;
	errorCode?: string;
	errorMessage?: string;
	errorSummary?: string;
	message?: string;
}

function getClaimUsageIngestionErrorPresentationByCode(
	errorCode: string | null | undefined,
): string {
	switch (errorCode) {
		case 'CLAIM_USAGE_BLOB_READ_FAILED':
			return 'Arquivo original indisponivel';
		case 'CLAIM_USAGE_BRONZE_WRITE_FAILED':
			return 'Falha ao gravar dados';
		case 'CLAIM_USAGE_BRONZE_RESUME_FAILED':
			return 'Falha ao retomar processamento';
		case 'CLAIM_USAGE_BRONZE_COMPLETENESS_FAILED':
			return 'Conferencia de completude falhou';
		case 'CLAIM_USAGE_HEADER_SCHEMA_DIVERGENT':
			return 'Layout do arquivo incompativel';
		case 'CLAIM_USAGE_PARSE_FAILED':
			return 'Erro de leitura do arquivo';
		case 'CLAIM_USAGE_INGESTION_FAILED':
			return 'Falha geral de processamento';
		default:
			return '';
	}
}

function getClaimUsageIngestionErrorPresentationByMessage(
	message: string | null | undefined,
): string {
	const normalizedMessage = message?.toLowerCase() ?? '';

	if (!normalizedMessage.trim()) {
		return 'Falha no processamento';
	}

	if (/databricks|hive|warehouse|bronze/.test(normalizedMessage)) {
		return 'Falha ao gravar dados';
	}

	if (/header|schema|columns|colunas|layout/.test(normalizedMessage)) {
		return 'Layout do arquivo incompativel';
	}

	if (/parse|linha|delimitador/.test(normalizedMessage)) {
		return 'Erro de leitura do arquivo';
	}

	return 'Falha no processamento';
}

export function getClaimUsageIngestionErrorPresentation(
	source: ClaimUsageIngestionErrorPresentationSource | null | undefined,
): string {
	const summary = source?.errorSummary?.trim();

	if (summary) {
		return summary;
	}

	const codePresentation = getClaimUsageIngestionErrorPresentationByCode(
		source?.errorCode,
	);

	if (codePresentation) {
		return codePresentation;
	}

	return getClaimUsageIngestionErrorPresentationByMessage(
		source?.errorMessage ?? source?.error ?? source?.message,
	);
}
